/**
 * Where a file lives under MEDIA_ROOT. Both the API and the worker write into
 * this layout now -- the worker when it fetches from Box, the API when someone
 * uploads from a phone -- so the rule has to be one rule, not two that agree
 * today. Paths are relative to the root so the volume can be moved without a
 * migration.
 */
export const paths = {
    original: (projectId, assetId, ext) => `projects/${projectId}/originals/${assetId}${ext}`,
    /**
     * Die Vorschau. Die Endung ist ein Argument, seit es Aufnahmen mit
     * Alphakanal gibt (S16): h.264 hat keinen, also liegt deren Vorschau als
     * WebM daneben -- gleicher Ort, gleicher Name, andere Endung.
     */
    proxy: (projectId, assetId, ext = '.mp4') => `projects/${projectId}/proxies/${assetId}${ext}`,
    poster: (projectId, assetId) => `projects/${projectId}/posters/${assetId}.jpg`,
    /**
     * The grid is in the name, so whoever draws the strip knows how to cut it
     * without a second place to look it up -- and a strip written by an older
     * version keeps its own name and its own reading.
     */
    filmstrip: (projectId, assetId, grid) => grid
        ? `projects/${projectId}/filmstrips/${assetId}-${grid.columns}x${grid.rows}.jpg`
        : `projects/${projectId}/filmstrips/${assetId}.jpg`,
    waveform: (projectId, assetId) => `projects/${projectId}/waveforms/${assetId}.json`,
    /** Wo die Einstellung wechselt (7.66d) -- eine Liste Sekunden je Medium. */
    szenen: (projectId, assetId) => `projects/${projectId}/szenen/${assetId}.json`,
    render: (projectId, renderId) => `projects/${projectId}/renders/${renderId}.mp4`,
    work: (jobId) => `work/${jobId}`,
    /**
     * Das Profilbild eines Menschen (7.45b).
     *
     * Bewusst neben `projects/` und `work/` und nicht darin: der Aufräumer
     * durchsucht genau diese beiden und wirft weg, worauf nichts zeigt. Ein
     * Profilbild gehört keinem Projekt und würde dort früher oder später
     * mitgenommen.
     */
    profilbild: (userId) => `profilbilder/${userId}.png`,
};
/**
 * The extension to store a file under, taken from its name. Anything that is
 * not a short, plain suffix becomes `.bin`: a name arriving from Box or from a
 * browser must never smuggle a path separator into the layout above.
 */
export function extensionOf(name) {
    const dot = name.lastIndexOf('.');
    const ext = dot > 0 ? name.slice(dot).toLowerCase() : '';
    return /^\.[a-z0-9]{1,8}$/.test(ext) ? ext : '.bin';
}
const FILMSTRIP_MAX_COLUMNS = 16;
export function filmstripGrid(durationSec) {
    const seconds = Math.max(durationSec, 0.1);
    const frames = Math.min(256, Math.max(12, Math.ceil(seconds * 2)));
    const columns = Math.min(FILMSTRIP_MAX_COLUMNS, frames);
    const rows = Math.ceil(frames / columns);
    return { frames: columns * rows, columns, rows, intervalSec: Math.max(seconds / (columns * rows + 1), 0.05) };
}
/**
 * The grid a stored strip was written with. A name without a grid comes from
 * the version that wrote one long row, one frame every two seconds.
 */
export function filmstripGridOf(path, durationSec) {
    // Not anchored to the end: a signed media url carries the path as a query
    // parameter, with the signature after it.
    const match = /-(\d+)x(\d+)\.jpg/.exec(path);
    if (!match) {
        const columns = Math.min(120, Math.max(6, Math.round(Math.max(durationSec, 0.1) / 2)));
        return { frames: columns, columns, rows: 1, intervalSec: Math.max(durationSec / columns, 0.05) };
    }
    const columns = Number(match[1]);
    const rows = Number(match[2]);
    const frames = columns * rows;
    return { frames, columns, rows, intervalSec: Math.max(Math.max(durationSec, 0.1) / (frames + 1), 0.05) };
}
/** Which frame of the strip shows the moment `seconds` into the recording. */
export function filmstripIndex(grid, seconds) {
    return Math.min(grid.frames - 1, Math.max(0, Math.floor(seconds / grid.intervalSec)));
}
//# sourceMappingURL=storage.js.map